import { AppDialog, DetailField, DetailGrid, StatusBadge } from "../../../components/ui";
import {
  POINT_RULE_ACTION_LABELS,
  POINT_RULE_KIND_LABELS,
  PointRule,
  formatPointRuleCondition,
  formatPointRuleValue,
  formatPointRuleValueCompact,
  getPointRuleDisplayName,
  isPointRuleWindowFinished,
  resolvePointRuleStreakPeriod,
} from "../../../models/pointRule";
import {
  useGetEventSubTypesQuery,
  useGetEventTypesQuery,
} from "../../../store/services/events-types";
import { toReportDateParam } from "../../../utils/constants/report-date-range";
import { formatDateWithoutSeconds } from "../../../utils/functions/date";
import { PointRulePreview } from "./PointRulePreview";
import { getPointRuleFieldApplicability, resolvePointRuleEventScopeNames } from "./pointRuleForm";

export interface PointRuleDetailDialogProps {
  tenantId: string;
  /** undefined = cerrado. */
  rule?: PointRule;
  /** Todas las reglas del tenant: de acá sale la regla que cuenta una racha. */
  rules?: PointRule[];
  onHide: () => void;
}

const yesNo = (value: boolean): string => (value ? "Sí" : "No");

/** Detalle de solo lectura de una regla (§6.3: un único botón "Cerrar"). */
export const PointRuleDetailDialog = ({
  tenantId,
  rule,
  rules = [],
  onHide,
}: PointRuleDetailDialogProps) => {
  const visible = rule !== undefined;
  const { data: eventTypes } = useGetEventTypesQuery({ tenantId }, { skip: !visible });
  const { data: eventSubTypes } = useGetEventSubTypesQuery({ tenantId }, { skip: !visible });

  const streakBaseRule =
    rule?.ruleKind === "streak"
      ? rules.find((candidate) => candidate.id === rule.baseRuleId)
      : undefined;

  const appliesToEvents =
    rule !== undefined &&
    getPointRuleFieldApplicability(rule.ruleKind, rule.actionType, rule.windowPeriod).eventScope;
  const { eventTypeName, eventSubTypeName } = resolvePointRuleEventScopeNames(
    { eventTypeId: rule?.eventTypeId ?? null, eventSubTypeId: rule?.eventSubTypeId ?? null },
    eventTypes,
    eventSubTypes
  );

  return (
    <AppDialog
      visible={visible}
      onHide={onHide}
      header={rule ? getPointRuleDisplayName(rule) : "Detalle de la regla"}
      variant="detail"
      style={{ width: "min(92vw, 880px)" }}
    >
      {rule && (
        <div className="grid">
          <div className="col-12 md:col-7 flex flex-column" style={{ gap: "var(--space-18)" }}>
            <DetailGrid>
              <DetailField label="Acción">{POINT_RULE_ACTION_LABELS[rule.actionType]}</DetailField>
              <DetailField label="Tipo de regla">{POINT_RULE_KIND_LABELS[rule.ruleKind]}</DetailField>
              <DetailField label="Puntos">{formatPointRuleValue(rule)}</DetailField>
              {rule.ruleKind === "streak" && (
                <DetailField label="Qué cuenta como semana de racha" wide>
                  {streakBaseRule
                    ? `${getPointRuleDisplayName(streakBaseRule)} · ${formatPointRuleCondition(streakBaseRule)}`
                    : "—"}
                </DetailField>
              )}
              {(rule.ruleKind === "threshold_window" || rule.ruleKind === "streak") && (
                <DetailField label={rule.ruleKind === "streak" ? "Cuándo paga" : "Umbral"} wide>
                  <span className="inline-flex flex-wrap align-items-center" style={{ gap: "var(--space-6)" }}>
                    {formatPointRuleCondition(rule, resolvePointRuleStreakPeriod(rule, rules))}
                    {isPointRuleWindowFinished(rule, toReportDateParam(new Date())) && (
                      <StatusBadge label="Finalizada" tone="info" />
                    )}
                  </span>
                </DetailField>
              )}
              {appliesToEvents && (
                <>
                  <DetailField label="Solo actividades gratuitas">{yesNo(rule.freeEventsOnly)}</DetailField>
                  <DetailField label="Tipo de evento">{eventTypeName}</DetailField>
                  <DetailField label="Subtipo de evento">{eventSubTypeName}</DetailField>
                </>
              )}
              <DetailField label="Estado">
                <StatusBadge
                  label={rule.active ? "Activa" : "Inactiva"}
                  tone={rule.active ? "success" : "error"}
                />
              </DetailField>
              <DetailField label="Visible en la app">{yesNo(rule.visibleToUser)}</DetailField>
              <DetailField label="Ya otorgó puntos">{yesNo(rule.hasAwardedPoints)}</DetailField>
              <DetailField label="Cuenta desde">
                {rule.effectiveFrom ? formatDateWithoutSeconds(rule.effectiveFrom) : "—"}
              </DetailField>
              <DetailField label="Última actualización">
                {rule.updatedAt ? formatDateWithoutSeconds(rule.updatedAt) : "—"}
              </DetailField>
              <DetailField label="Descripción en la app" wide>
                {rule.displayDescription ?? "—"}
              </DetailField>
            </DetailGrid>
          </div>
          <div className="col-12 md:col-5">
            <PointRulePreview
              actionType={rule.actionType}
              iconKey={rule.iconKey}
              title={rule.displayTitle}
              description={rule.displayDescription}
              valueLabel={formatPointRuleValueCompact(rule)}
              isPenalty={rule.isPenalty}
              visibleToUser={rule.visibleToUser}
            />
          </div>
        </div>
      )}
    </AppDialog>
  );
};
